import React, { useEffect, useRef, useState, useCallback } from "react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import {
  Upload,
  Download,
  Trash2,
  Image as ImageIcon,
  FileText,
  Eye,
  Folder,
  FolderPlus,
  Pencil,
  FolderInput,
  MoreHorizontal,
  Link2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@b2b/ui-components";

// Placeholder image as data URI (no external request needed)
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23e5e7eb' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='18' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";

interface MediaManagementProps {
  role?: "admin" | "seller";
}

interface MediaFolder {
  name: string;
  slug: string;
  count: number;
}
interface MoveTarget {
  ids: string[];
  names: string[];
  folder?: string;
}

const PAGE_SIZE = 20;
const DEFAULT_FOLDER = "uploads";
const MEDIA_LIBRARY_ONLY = true;
const MEDIA_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
const MEDIA_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/svg+xml,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.svg,.pdf";

/** Radix DropdownMenu + Dialog can leave body pointer-events locked; defer dialog open until menu closes. */
const runAfterDropdownClose = (action: () => void) => {
  window.setTimeout(action, 0);
};

const isLibraryMediaFile = (file: File): boolean => {
  const mime = file.type?.toLowerCase() || "";
  if (mime.startsWith("image/") || mime === "application/pdf") {
    return true;
  }
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  return ["jpg", "jpeg", "png", "webp", "gif", "svg", "pdf"].includes(ext);
};

const MediaManagement = ({ role: roleProp }: MediaManagementProps) => {
  const isAdmin = roleProp !== "seller";
  const mediaService = isAdmin ? adminService : sellerService;
  const [mediaFiles, setMediaFiles] = useState<any[]>([]);
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [editFolderOpen, setEditFolderOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<MediaFolder | null>(null);
  const [updatingFolder, setUpdatingFolder] = useState(false);
  const [deletingFolderSlug, setDeletingFolderSlug] = useState<string | null>(null);
  const [moveMediaOpen, setMoveMediaOpen] = useState(false);
  const [movingMedia, setMovingMedia] = useState(false);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [moveFolderSlug, setMoveFolderSlug] = useState(DEFAULT_FOLDER);
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadingRef = useRef(false);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const loadFolders = useCallback(async () => {
    setFoldersLoading(true);
    try {
      const list = isAdmin
        ? await adminService.listMediaFolders(undefined, MEDIA_LIBRARY_ONLY)
        : await sellerService.listMediaFolders(MEDIA_LIBRARY_ONLY);
      setFolders(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error loading media folders:", error);
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [isAdmin]);

  const loadMedia = useCallback(
    async (pageNum: number = page, folderSlug: string | null = selectedFolder) => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      try {
        const res = isAdmin
          ? await adminService.listMedia(
              pageNum,
              PAGE_SIZE,
              undefined,
              undefined,
              folderSlug || undefined,
              MEDIA_LIBRARY_ONLY
            )
          : await sellerService.listMedia(
              pageNum,
              PAGE_SIZE,
              undefined,
              folderSlug || undefined,
              MEDIA_LIBRARY_ONLY
            );

        if (res && typeof res === "object") {
          if (Array.isArray((res as any).data)) {
            setMediaFiles((res as any).data);
            setTotal((res as any).total || 0);
          } else if ((res as any).data && Array.isArray((res as any).data.data)) {
            setMediaFiles((res as any).data.data);
            setTotal((res as any).data.total || 0);
          } else {
            setMediaFiles([]);
            setTotal(0);
          }
        } else {
          setMediaFiles([]);
          setTotal(0);
        }
      } catch (error) {
        console.error("Error loading media:", error);
        setMediaFiles([]);
        setTotal(0);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [isAdmin, page, selectedFolder]
  );

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    loadMedia(page, selectedFolder);
  }, [loadMedia, page, selectedFolder]);

  useEffect(() => {
    setSelectedMediaIds([]);
  }, [page, selectedFolder, mediaFiles.length]);

  const handleFolderSelect = (slug: string | null) => {
    setSelectedFolder(slug);
    setPage(1);
  };

  const handleCreateFolder = async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      showToast("error", "Folder name is required");
      return;
    }

    setCreatingFolder(true);
    try {
      const created = await mediaService.createMediaFolder(trimmed);
      showToast("success", `Folder "${created.name}" created`);
      setNewFolderName("");
      setCreateFolderOpen(false);
      await loadFolders();
      setSelectedFolder(created.slug);
      setPage(1);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create folder";
      showToast("error", message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const openEditFolder = (folder: MediaFolder) => {
    setEditingFolder(folder);
    setNewFolderName(folder.name);
    setEditFolderOpen(true);
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder) return;
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      showToast("error", "Folder name is required");
      return;
    }

    setUpdatingFolder(true);
    try {
      const updated = await mediaService.updateMediaFolder(
        editingFolder.slug,
        trimmed
      );
      showToast("success", `Folder renamed to "${updated.name}"`);
      setEditFolderOpen(false);
      setEditingFolder(null);
      setNewFolderName("");
      await Promise.all([loadFolders(), loadMedia(page, selectedFolder)]);
      if (selectedFolder === editingFolder.slug) {
        setSelectedFolder(updated.slug);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update folder";
      showToast("error", message);
    } finally {
      setUpdatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folder: MediaFolder) => {
    if (folder.slug === DEFAULT_FOLDER) return;
    const message =
      folder.count > 0
        ? `Delete "${folder.name}"? ${folder.count} file(s) will move to General.`
        : `Delete folder "${folder.name}"?`;
    if (!confirm(message)) return;

    setDeletingFolderSlug(folder.slug);
    try {
      const result = await mediaService.deleteMediaFolder(folder.slug);
      showToast(
        "success",
        result.movedCount > 0
          ? `Folder deleted. ${result.movedCount} file(s) moved to General.`
          : "Folder deleted"
      );
      const wasSelected = selectedFolder === folder.slug;
      if (wasSelected) {
        setSelectedFolder(null);
        setPage(1);
      }
      await loadFolders();
      await loadMedia(wasSelected ? 1 : page, wasSelected ? null : selectedFolder);
    } catch (error: any) {
      const errMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete folder";
      showToast("error", errMessage);
    } finally {
      setDeletingFolderSlug(null);
    }
  };

  const openMoveMedia = (file: any) => {
    setMoveTarget({
      ids: [file.id],
      names: [file.originalName],
      folder: file.folder,
    });
    setMoveFolderSlug(file.folder || DEFAULT_FOLDER);
    setMoveMediaOpen(true);
  };

  const toggleMediaSelection = (id: string) => {
    setSelectedMediaIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const toggleSelectAllCurrentPage = () => {
    if (selectedMediaIds.length === mediaFiles.length) {
      setSelectedMediaIds([]);
      return;
    }
    setSelectedMediaIds(mediaFiles.map((file) => file.id));
  };

  const openMoveSelectedMedia = () => {
    if (selectedMediaIds.length === 0) {
      return;
    }
    const selectedFiles = mediaFiles.filter((file) => selectedMediaIds.includes(file.id));
    setMoveTarget({
      ids: selectedFiles.map((file) => file.id),
      names: selectedFiles.map((file) => file.originalName),
      folder: selectedFiles[0]?.folder,
    });
    setMoveFolderSlug(selectedFiles[0]?.folder || DEFAULT_FOLDER);
    setMoveMediaOpen(true);
  };

  const handleDeleteSelectedMedia = async () => {
    if (selectedMediaIds.length === 0) {
      return;
    }
    const totalSelected = selectedMediaIds.length;
    if (
      !confirm(
        `Delete ${totalSelected} selected media file${totalSelected > 1 ? "s" : ""}? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const deletedIds = await mediaService.deleteMultipleMedia(selectedMediaIds);
      const successCount = deletedIds.length;
      const failedCount = totalSelected - successCount;
      if (successCount > 0 && failedCount === 0) {
        showToast(
          "success",
          `${successCount} media file${successCount > 1 ? "s" : ""} deleted successfully`
        );
      } else if (successCount > 0) {
        showToast(
          "error",
          `${successCount} deleted, ${failedCount} failed. Please retry failed items.`
        );
      } else {
        showToast("error", "Failed to delete selected media");
      }
      setSelectedMediaIds([]);
      await Promise.all([loadMedia(page, selectedFolder), loadFolders()]);
    } catch (error) {
      console.error("Bulk delete failed:", error);
      showToast("error", "Failed to delete selected media");
    }
  };

  const handleMoveMedia = async () => {
    if (!moveTarget) return;

    setMovingMedia(true);
    try {
      const successResult = await mediaService.moveMultipleMediaToFolder(
        moveTarget.ids,
        moveFolderSlug
      );
      const successCount = successResult.length;
      const failedCount = moveTarget.ids.length - successCount;
      if (successCount > 0 && failedCount === 0) {
        showToast(
          "success",
          `${successCount} media file${successCount > 1 ? "s" : ""} moved successfully`
        );
      } else if (successCount > 0) {
        showToast(
          "error",
          `${successCount} moved, ${failedCount} failed. Please retry failed items.`
        );
      } else {
        showToast("error", "Failed to move selected media");
      }
      setMoveMediaOpen(false);
      setMoveTarget(null);
      setSelectedMediaIds([]);
      await Promise.all([loadMedia(page, selectedFolder), loadFolders()]);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to move media";
      showToast("error", message);
    } finally {
      setMovingMedia(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploadFolder = selectedFolder || DEFAULT_FOLDER;
      let uploadedCount = 0;
      let skippedCount = 0;
      let oversizedCount = 0;
      for (const f of Array.from(files)) {
        if (!isLibraryMediaFile(f)) {
          skippedCount += 1;
          continue;
        }
        if (f.size > MEDIA_MAX_FILE_SIZE_BYTES) {
          oversizedCount += 1;
          continue;
        }
        await mediaService.uploadMedia(f, uploadFolder, MEDIA_LIBRARY_ONLY);
        uploadedCount += 1;
      }
      await Promise.all([loadMedia(page, selectedFolder), loadFolders()]);
      if (uploadedCount > 0) {
        showToast(
          "success",
          `${uploadedCount} file${uploadedCount > 1 ? "s" : ""} uploaded successfully`
        );
      }
      if (skippedCount > 0) {
        showToast(
          "error",
          `${skippedCount} file${skippedCount > 1 ? "s were" : " was"} skipped. Only images and PDF are allowed.`
        );
      }
      if (oversizedCount > 0) {
        showToast(
          "error",
          `${oversizedCount} file${oversizedCount > 1 ? "s exceed" : " exceeds"} the 2MB size limit.`
        );
      }
      if (uploadedCount === 0 && skippedCount === 0 && oversizedCount === 0) {
        showToast("error", "No files were uploaded.");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      showToast("error", "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this media?")) return;
    await mediaService.deleteMedia(id);
    await Promise.all([loadMedia(page, selectedFolder), loadFolders()]);
  };

  const handleDownload = async (id: string, filename: string) => {
    try {
      await mediaService.downloadMediaFile(id, filename);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const openImagePreview = (file: any) => {
    const previewUrl = file.url || file.thumbnailUrl || PLACEHOLDER_IMAGE;
    setPreviewImage({ url: previewUrl, name: file.originalName || "Image preview" });
  };

  const openPdfPreview = (file: any) => {
    const url = file.url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleViewMedia = (file: any) => {
    if (isPdf(file.mimeType, file.originalName)) {
      openPdfPreview(file);
      return;
    }
    if (isImage(file.mimeType)) {
      openImagePreview(file);
      return;
    }
    if (file.url) {
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopyUrl = async (file: any) => {
    const url = file.url || file.thumbnailUrl;
    if (!url) {
      showToast("error", "No URL available to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast("success", "URL copied to clipboard");
    } catch {
      showToast("error", "Failed to copy URL");
    }
  };

  const isImage = (mime: string) => mime?.startsWith("image/");
  const isPdf = (mime: string, name?: string) =>
    mime === "application/pdf" || name?.toLowerCase().endsWith(".pdf");
  const fileSize = (b: number) => {
    if (!b) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(b) / Math.log(k));
    return `${(b / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;
  const allMediaCount = folders.reduce((sum, folder) => sum + folder.count, 0);
  const activeFolderLabel =
    selectedFolder === null
      ? folders.find((folder) => folder.slug === DEFAULT_FOLDER)?.name || "General"
      : folders.find((folder) => folder.slug === selectedFolder)?.name ||
        "Selected folder";

  const paginationButtonClass =
    "px-3 py-1 border rounded transition-colors enabled:hover:bg-gray-50 " +
    "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none";

  return (
    <div className="space-y-6 p-4">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6">
        <aside className="border rounded-lg p-4 bg-white h-fit min-w-0 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Folders</h2>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
              onClick={() => setCreateFolderOpen(true)}
            >
              <FolderPlus className="w-4 h-4" />
              New
            </button>
          </div>

          {foldersLoading ? (
            <div className="py-4 text-sm text-gray-500">Loading folders...</div>
          ) : (
            <div className="space-y-1 min-w-0">
              <button
                type="button"
                onClick={() => handleFolderSelect(null)}
                className={`w-full min-w-0 flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                  selectedFolder === null
                    ? "bg-orange-50 text-orange-700 border border-orange-200"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <span className="inline-flex min-w-0 flex-1 items-center gap-2">
                  <Folder className="w-4 h-4 shrink-0" />
                  <span className="truncate">All folders</span>
                </span>
                <span className="shrink-0 text-xs text-gray-500 tabular-nums">
                  {allMediaCount}
                </span>
              </button>

              {folders.map((folder) => (
                <div
                  key={folder.slug}
                  className={`group flex min-w-0 w-full items-center gap-0.5 rounded-md transition-colors ${
                    selectedFolder === folder.slug
                      ? "bg-orange-50 border border-orange-200"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleFolderSelect(folder.slug)}
                    className={`min-w-0 flex-1 flex items-center gap-2 overflow-hidden rounded-md px-3 py-2 text-sm text-left ${
                      selectedFolder === folder.slug
                        ? "text-orange-700"
                        : "text-gray-700"
                    }`}
                  >
                    <Folder className="w-4 h-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate" title={folder.name}>
                      {folder.name}
                    </span>
                    <span className="shrink-0 text-xs text-gray-500 tabular-nums">
                      {folder.count}
                    </span>
                  </button>
                  {folder.slug !== DEFAULT_FOLDER && (
                    <div className="flex shrink-0 items-center pr-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-white text-gray-500 hover:text-orange-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditFolder(folder);
                        }}
                        aria-label={`Edit ${folder.name}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-white text-gray-500 hover:text-red-600 disabled:opacity-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFolder(folder);
                        }}
                        disabled={deletingFolderSlug === folder.slug}
                        aria-label={`Delete ${folder.name}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </aside>

        <div className="space-y-6">
          <div className="border-2 border-dashed rounded-lg p-8 text-center bg-white">
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="mb-1">Upload Images / PDF</p>
            <p className="text-sm text-gray-500 mb-4">
              Uploading to: <span className="font-medium">{activeFolderLabel}</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={MEDIA_ACCEPT}
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              className="px-4 py-2 rounded bg-black text-white"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Select Files"}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Images and PDF only, up to 2MB each
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">
              {isAdmin ? "Media Library" : "My Media Library"} ({total})
            </h2>
            {!loading && mediaFiles.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={toggleSelectAllCurrentPage}
                >
                  {selectedMediaIds.length === mediaFiles.length
                    ? "Unselect all"
                    : "Select all"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={openMoveSelectedMedia}
                  disabled={selectedMediaIds.length === 0}
                >
                  Move selected ({selectedMediaIds.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDeleteSelectedMedia}
                  disabled={selectedMediaIds.length === 0}
                >
                  Delete selected ({selectedMediaIds.length})
                </Button>
              </div>
            )}
            {loading ? (
              <div className="py-8 text-center">Loading...</div>
            ) : !Array.isArray(mediaFiles) || mediaFiles.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No media found</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {mediaFiles.map((file) => (
                  <div key={file.id} className="border rounded overflow-hidden bg-white">
                    <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                      <label
                        className="absolute top-1.5 left-1.5 z-10 inline-flex items-center justify-center rounded bg-white/95 border border-gray-200 p-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMediaIds.includes(file.id)}
                          onChange={() => toggleMediaSelection(file.id)}
                          aria-label={`Select ${file.originalName}`}
                        />
                      </label>
                      {isImage(file.mimeType) ? (
                        <img
                          src={file.thumbnailUrl || file.url || PLACEHOLDER_IMAGE}
                          alt={file.originalName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (
                              file.url &&
                              file.url.includes("digitaloceanspaces.com")
                            ) {
                              target.src = file.url.split("?")[0];
                            }
                          }}
                        />
                      ) : isPdf(file.mimeType, file.originalName) ? (
                        <div className="flex flex-col items-center justify-center gap-1 text-gray-500 px-2">
                          <FileText className="w-8 h-8" />
                          <span className="text-[10px] uppercase tracking-wide">PDF</span>
                        </div>
                      ) : (
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      )}

                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="absolute top-1.5 right-1.5 z-10 inline-flex items-center justify-center rounded-md bg-white/95 p-1.5 shadow-sm border border-gray-200 hover:bg-white"
                            aria-label={`Actions for ${file.originalName}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="w-4 h-4 text-gray-700" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44 bg-white">
                          <DropdownMenuItem
                            onSelect={() =>
                              runAfterDropdownClose(() => handleViewMedia(file))
                            }
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() =>
                              runAfterDropdownClose(() => openMoveMedia(file))
                            }
                          >
                            <FolderInput className="w-4 h-4" />
                            Move
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDownload(file.id, file.originalName)}
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCopyUrl(file)}>
                            <Link2 className="w-4 h-4" />
                            Copy URL
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => handleDelete(file.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="p-2 text-xs">
                      <div className="truncate" title={file.originalName}>
                        {file.originalName}
                      </div>
                      <div className="text-gray-500">{fileSize(file.size)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {total > PAGE_SIZE && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  type="button"
                  className={paginationButtonClass}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={isFirstPage}
                >
                  Prev
                </button>
                <span className="text-sm">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className={paginationButtonClass}
                  onClick={() => setPage((p) => p + 1)}
                  disabled={isLastPage}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create media folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder="Folder name (e.g. Product Images)"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              maxLength={64}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateFolder();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateFolderOpen(false);
                  setNewFolderName("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={creatingFolder || !newFolderName.trim()}
                onClick={handleCreateFolder}
              >
                {creatingFolder ? "Creating..." : "Create folder"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editFolderOpen}
        onOpenChange={(open) => {
          setEditFolderOpen(open);
          if (!open) {
            setEditingFolder(null);
            setNewFolderName("");
          }
        }}
      >
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              maxLength={64}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleUpdateFolder();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditFolderOpen(false);
                  setEditingFolder(null);
                  setNewFolderName("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={updatingFolder || !newFolderName.trim()}
                onClick={handleUpdateFolder}
              >
                {updatingFolder ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={moveMediaOpen}
        onOpenChange={(open) => {
          setMoveMediaOpen(open);
          if (!open) setMoveTarget(null);
        }}
      >
        <DialogContent className="bg-white w-full max-w-md overflow-hidden min-w-0">
          <DialogHeader className="pr-8">
            <DialogTitle>Move to folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2 min-w-0 w-full overflow-hidden">
            {moveTarget && (
              <div className="min-w-0 rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500 mb-1">
                  Moving {moveTarget.ids.length > 1 ? "files" : "file"}
                </p>
                <p className="text-sm font-medium text-gray-900 break-all line-clamp-2">
                  {moveTarget.names[0]}
                  {moveTarget.names.length > 1
                    ? ` + ${moveTarget.names.length - 1} more`
                    : ""}
                </p>
              </div>
            )}
            <select
              className="w-full min-w-0 max-w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
              value={moveFolderSlug}
              onChange={(e) => setMoveFolderSlug(e.target.value)}
            >
              {folders.map((folder) => (
                <option key={folder.slug} value={folder.slug}>
                  {folder.name}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setMoveMediaOpen(false);
                  setMoveTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={movingMedia || !moveTarget}
                onClick={handleMoveMedia}
              >
                {movingMedia
                  ? "Moving..."
                  : `Move ${moveTarget && moveTarget.ids.length > 1 ? "media" : "file"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="!w-fit !max-w-[92vw] bg-white">
          <DialogHeader className="pr-8">
            <DialogTitle className="text-left break-all">
              {previewImage?.name || "Image preview"}
            </DialogTitle>
          </DialogHeader>
          {previewImage && (
            <div className="flex items-center justify-center overflow-hidden">
              <img
                src={previewImage.url}
                alt={previewImage.name}
                className="block w-auto h-auto max-w-[88vw] max-h-[75vh] object-contain rounded"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (previewImage.url.includes("digitaloceanspaces.com")) {
                    target.src = previewImage.url.split("?")[0];
                  } else {
                    target.src = PLACEHOLDER_IMAGE;
                  }
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaManagement;
export { MediaManagement as AdminMediaManagement };
